
import _ from 'lodash';
import MarkdownIt from 'markdown-it';

import {
  EditorState,
  Plugin,
} from 'prosemirror-state';
import {
  EditorView,
} from 'prosemirror-view';
import {
  liftListItem,
  sinkListItem,
  wrapInList,
} from 'prosemirror-schema-list';
import {
  exampleSetup,
} from 'prosemirror-example-setup';
import {
  wrapIn,
  toggleMark,
  setBlockType,
  lift,
} from 'prosemirror-commands';
import {
  defaultMarkdownParser,
  defaultMarkdownSerializer,
  MarkdownParser,
} from 'prosemirror-markdown';
import {
  Schema,
  DOMSerializer,
} from 'prosemirror-model';

import utils from 'app/helpers/utils';

const maxHLevel = 4;

const nodes = {
  doc: {
    content: 'block+'
  },
  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [{tag: 'p'}],
    toDOM: () => ['p', 0],
  },
  blockquote: {
    content: 'block+',
    group: 'block',
    parseDOM: [{tag: 'blockquote'}],
    toDOM: () => ['blockquote', 0],
  },
  horizontal_rule: {
    group: 'block',
    parseDOM: [{tag: 'hr'}],
    toDOM: () => ['div', ['hr']],
  },
  heading: {
    attrs: {level: {default: 1}},
    // content: '(text | image)*',
    content: 'text*',
    group: 'block',
    defining: true,
    parseDOM: [
      {tag: 'h1', attrs: {level: 1}},
      {tag: 'h2', attrs: {level: 2}},
      {tag: 'h3', attrs: {level: 3}},
      {tag: 'h4', attrs: {level: 4}},
      {tag: 'h5', attrs: {level: 5}},
      {tag: 'h6', attrs: {level: 6}},
    ],
    toDOM: (node) => {
      const level = Math.min(node.attrs.level, maxHLevel);
      return [`h${level}`, 0];
    },
  },
  code_block: {
    content: 'text*',
    group: 'block',
    code: true,
    defining: true,
    marks: '',
    attrs: {params: {default: ''}},
    parseDOM: [{
      tag: 'pre',
      preserveWhitespace: 'full',
      getAttrs: (node) => ({
        params: node.getAttribute('data-params') || '',
      }),
    }],
    toDOM: (node) => [
      'pre',
      node.attrs.params ? {'data-params': node.attrs.params} : {},
      ['code', 0],
    ],
  },
  ordered_list: {
    content: 'list_item+',
    group: 'block',
    attrs: {order: {default: 1}, tight: {default: true}},
    parseDOM: [{tag: 'ol', getAttrs: (dom) => ({
      order: ((dom).hasAttribute('start') ? +(dom).getAttribute('start') : 1),
      tight: (dom).hasAttribute('data-tight'),
    })}],
    toDOM: (node) => [
      'ol',
      {start: (node.attrs.order == 1 ? null : node.attrs.order), 'data-tight': (node.attrs.tight ? 'true' : null)},
      0
    ],
  },
  bullet_list: {
    content: 'list_item+',
    group: 'block',
    attrs: {tight: {default: true}},
    parseDOM: [{tag: 'ul', getAttrs: (dom) => ({tight: (dom).hasAttribute('data-tight')})}],
    toDOM: (node) => ['ul', {'data-tight': node.attrs.tight ? 'true' : null}, 0],
  },
  list_item: {
    content: 'block+',
    defining: true,
    parseDOM: [{tag: 'li'}],
    toDOM: () => ['li', 0],
  },
  text: {
    group: 'inline'
  },
  // images converted to links
  image: {
    inline: true,
    attrs: {
      src: {},
      alt: {default: null},
      title: {default: null},
      target: {default: '_blank'},
    },
    group: 'inline',
    // draggable: true,
    parseDOM: [{tag: 'img[src]', getAttrs: (dom) => ({
      src: (dom).getAttribute('src'),
      title: (dom).getAttribute('title'),
      alt: (dom).getAttribute('alt'),
      target: '_blank',
    })}],
    toDOM: (node) => {
      const title = node.attrs.title || node.attrs.alt || '';
      const attrs = {href: node.attrs.src, target: '_blank', rel: 'noreferrer'};
      return ['a', attrs, title];
    },
  },
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{tag: 'br'}],
    toDOM: () => ['br'],
  }
};

const marks = {
  em: {
    parseDOM: [
      {tag: 'i'}, {tag: 'em'},
      {style: 'font-style=italic'},
      {style: 'font-style=normal', clearMark: m => m.type.name == 'em'}
    ],
    toDOM: () => ['em'],
  },
  strong: {
    parseDOM: [
      {tag: 'strong'},
      {tag: 'b', getAttrs: node => node.style.fontWeight != 'normal' && null},
      {style: 'font-weight=400', clearMark: m => m.type.name == 'strong'},
      {style: 'font-weight', getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null}
    ],
    toDOM: () => ['strong'],
  },
  link: {
    attrs: {
      href: {},
      title: {default: null},
    },
    inclusive: false,
    parseDOM: [{tag: 'a[href]', getAttrs: (dom) => ({
      href: dom.getAttribute('href'),
      title: dom.getAttribute('title'),
    })}],
    toDOM: (node) => ['a', {...node.attrs, target: '_blank', rel: 'noreferrer'}],
  },
  code: {
    code: true,
    parseDOM: [{tag: 'code'}],
    toDOM: () => ['code'],
  },
};

const noop = () => {};



/*
 *  main runtime objects
 */

const schema = new Schema({nodes, marks});
const markLink = schema.marks.link;

const listenerPlugin = (onUpdate=noop) => new Plugin({
  view: (editorView) => ({update: onUpdate, destroy: noop}),
});

const makeState = (onUpdate=()=>{}, md='') => {
  return EditorState.create({
    doc: mdToDoc(md),
    plugins: [
      listenerPlugin(onUpdate),
      ...exampleSetup({schema, menuBar: false}),
    ],
  });  
};

const makeView = (elEditor, initialMd='', onUpdate=noop) => {
  const state = makeState(onUpdate, (initialMd || ''));
  return new EditorView(elEditor, {state});
};



/*
 *  internal utils
 */

const getSelectedNodes = (state) => {
  const nodes = [];
  state.doc.nodesBetween(state.selection.from, state.selection.to, (node) => {
    nodes.push(node);
  })
  return nodes;
};

const filterNodes = (nodes, filter=()=>true) => {
  return nodes.filter(filter);
};

const filterNodesWithMark = (nodes, mark) => {
  return filterNodes(nodes, (node) => {
    return mark.isInSet(node.marks);
  });
};

const hrefFromLinkNode = (node) => {
  const marks = [...(node.marks)];
  const linkMark = marks.find(m => m.type?.name === 'link');
  return linkMark?.attrs?.href || '';
};



/*
 *  external utils
 */

const defaultMenuState = {
  textType: 'paragraph',
  strong: {enabled: true, active: false, partiallyActive: false},
  em:     {enabled: true, active: false, partiallyActive: false},
  code:   {enabled: true, active: false, partiallyActive: false},
  link:   {enabled: true, active: false, partiallyActive: false, href: null},
  lists: {
    canListifyBullet: false,
    canListifyNumber: false,
    canLift: false,
    canSink: false,
    isInList: false,
  },
  actions: {
    canLift: false,
    canQuote: false,
  }
};

const getMenuState = (view) => {
  const {state, dispatch} = view;
  const hasSelection = !state.selection.empty;
  const canRunCmd = (cmdFn) => !!cmdFn(state, null, view);
  const canToggleMark = (mark) => canRunCmd(toggleMark(mark)) && hasSelection;
  // gather nodes in selection
  const nodes = getSelectedNodes(state);
  const textNodes = filterNodes(nodes, n => n.isText);
  const textBlockNodes = filterNodes(nodes, n => n.isTextblock);
  // basic mark states
  const menuState = ['strong', 'em', 'code'].reduce((acc, markName) => {
    const mark = schema.marks[markName];
    const markNodes = filterNodesWithMark(textNodes, mark);
    acc[markName] = {
      enabled: canToggleMark(mark),
      active: (markNodes.length === textNodes.length) && (textNodes.length > 0),
      partiallyActive: !!(markNodes.length && markNodes.length !== textNodes.length),
    };
    return acc;
  }, {});
  // text block type
  const textBlockTypes = [...new Set(textBlockNodes.map(n => `${n.type.name}${n.attrs?.level || ''}`))];;
  menuState.textType = (textBlockTypes.length === 1) ? textBlockTypes[0] : null;
  // link mark state
  const linkNodes = filterNodesWithMark(textNodes, markLink);
  const linkActive = linkNodes.length === 1 && textNodes.length === 1;
  const href = linkActive ? hrefFromLinkNode(linkNodes[0]) : null;
  menuState.link = {
    enabled: canToggleMark(markLink) || !!href,
    active: linkActive,
    partiallyActive: linkNodes.length >= 1 && textNodes.length > 1,
    href,
  };
  // lists
  const canLift = canRunCmd(liftListItem(schema.nodes.list_item));
  const canSink = canRunCmd(sinkListItem(schema.nodes.list_item));
  const isInList = canLift || canSink;
  const canListifyBullet = !isInList && canRunCmd(wrapInList(schema.nodes.bullet_list));
  const canListifyNumber = !isInList && canRunCmd(wrapInList(schema.nodes.ordered_list));
  menuState.lists = {canListifyBullet, canListifyNumber, canLift, canSink, isInList};
  // actions
  menuState.actions = (() => {
    const canLift = canRunCmd(lift);
    const canQuote = canRunCmd(wrapIn(schema.nodes.blockquote));
    return {canLift, canQuote};
  })();

  return menuState;
};

const areCoordsEquallish = (aCoords, bCoords) => {
  if (!aCoords && !bCoords) return true;
  if (!aCoords || !bCoords) return false;
  const aTop = Math.round(aCoords.top);
  const bTop = Math.round(bCoords.top);
  if (aTop !== bTop) return false;
  const aLeft = Math.round(aCoords.left);
  const bLeft = Math.round(bCoords.left);
  return aLeft === bLeft;
};

/**
 * Provides a ref to the mark of the given type within the current selection, as well as from+to position for entire mark
 * Via: https://discuss.prosemirror.net/t/expanding-the-selection-to-the-active-mark/478/9
 */
const getSelectionMarkInfo = (state, markType, pos=state.selection.from) => {
  const $pos = state.doc.resolve(pos);

  const { parent, parentOffset } = $pos;
  const start = parent.childAfter(parentOffset);
  if (!start.node) return {};

  const mark = start.node.marks.find((mark) => mark.type === markType);
  if (!mark) return {};

  let startIndex = $pos.index();
  let from = $pos.start() + start.offset;
  let endIndex = startIndex + 1;
  let to = from + start.node.nodeSize;
  while (startIndex > 0 && mark.isInSet(parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    from -= parent.child(startIndex).nodeSize;
  }
  while (endIndex < parent.childCount && mark.isInSet(parent.child(endIndex).marks)) {
    to += parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return { from, to, mark };
};

const hasContent = (view) => {
  if (view.state.doc.textContent?.length) return true;
  if (view.state.doc.firstChild?.type?.name !== 'paragraph') return true;
  return false;
};



/*
 *  Parse & Serialize
 */

const mdParseTokens = _.pickBy(defaultMarkdownParser.tokens, (val, key) => {
  if (nodes[val.block]) return true;
  if (nodes[val.node]) return true;
  if (marks[val.mark]) return true;
  return false;
});

// to disable things:
// const tokenizer = MarkdownIt('commonmark', {html: false}).disable(['list', 'blockquote']);
// see here for list of rules: https://github.com/markdown-it/markdown-it/blob/master/lib/presets/commonmark.mjs
const tokenizer = MarkdownIt('commonmark', {html: false});
const mdParser = new MarkdownParser(schema, tokenizer, mdParseTokens);
const mdSerializer = defaultMarkdownSerializer;
const domSerializer = DOMSerializer.fromSchema(schema);

const docToMd = (pmDoc) => {
  return mdSerializer.serialize(pmDoc, {tightLists: true});
};

const mdToDoc = (md='') => {
  return mdParser.parse(md);
};

const mdToHtml = (md='') => {
  const windowRef = utils.getWindow();
  const documentRef = windowRef.document;
  const pmDoc = mdToDoc(md);
  const domFrag = domSerializer.serializeFragment(pmDoc.content, {document: documentRef});
  const div = documentRef.createElement('div');
  div.appendChild(domFrag);
  const dirtyHtml = div.innerHTML;
  const cleanHtml = utils.sanitizeHtml(dirtyHtml, windowRef);
  // return dirtyHtml;
  return cleanHtml;
};

const mdToTxt = (md='') => {
  const pmDoc = mdToDoc(md);
  return pmDoc.textBetween(0, pmDoc.nodeSize - 2, '\n');
};



export default {
  // commands
  liftListItem,
  sinkListItem,
  wrapInList,
  wrapIn,
  toggleMark,
  setBlockType,
  lift,
  // main entities
  schema,
  markLink,
  makeState,
  makeView,
  // helpers
  defaultMenuState,
  getMenuState,
  areCoordsEquallish,
  getSelectionMarkInfo,
  hasContent,
  // parsers & serializers
  docToMd,
  mdToDoc,
  mdToHtml,
  mdToTxt,
};
